import { useState, type FormEvent } from 'react'
import { TextLink } from '@/components/ui/text-link'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Icon } from '@/components/ui/icon'
import { faCircleExclamation } from '@awesome.me/kit-e215ba4646/icons/sharp/light'
import { InviteOnlyMessages as M } from '@/constants/messages'
import {
  inviteLandlord,
  type LandlordInviteResult,
} from '@/services/auth/landlordInvite'
import { Spinner } from '@/components/ui/spinner'

const ERRORS: Partial<Record<LandlordInviteResult, string>> = {
  'invalid-email': M.invalidEmail,
  'same-email': M.sameEmail,
  'too-many': M.tooMany,
  failed: M.inviteFailed,
}

/**
 * Shown when a sign-in or sign-up is refused because the address has no
 * invitation (invite-only, PB-64). The page can't tell a tenant from someone
 * who manages properties, so it speaks to both: property managers request
 * access; a tenant (whose landlord evidently isn't on MNTND, or they'd have
 * been invited) can have MNTND email their landlord (Q14 B).
 */
export function NoInvitationNotice({ email }: { email: string }) {
  const [open, setOpen] = useState(false)
  const [landlordEmail, setLandlordEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<LandlordInviteResult | null>(null)

  const send = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSending(true)
    setResult(await inviteLandlord(email, landlordEmail.trim()))
    setSending(false)
  }

  return (
    <div className="space-y-3">
      <Alert variant="destructive">
        <Icon icon={faCircleExclamation} className="h-4 w-4" />
        <AlertTitle className="break-all">
          {M.noInvitationTitle(email)}
        </AlertTitle>
        <AlertDescription>{M.noInvitationBody}</AlertDescription>
      </Alert>

      <div className="space-y-3 rounded-lg border bg-card p-4 text-sm text-card-foreground">
        <p>
          {M.manageProperties}{' '}
          <TextLink to="/signup">{M.requestAccess}</TextLink>
        </p>

        {email && (
          <div className="space-y-3">
            <p>{M.renting}</p>
            {result === 'sent' ? (
              <p className="break-words text-success">
                {M.inviteSent(landlordEmail.trim())}
              </p>
            ) : !open ? (
              <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
                {M.inviteThem}
              </Button>
            ) : (
              <form onSubmit={send} className="space-y-2">
                <Label htmlFor="landlord-email">{M.landlordEmailLabel}</Label>
                <Input
                  autoComplete="email"
                  id="landlord-email"
                  type="email"
                  required
                  autoFocus
                  value={landlordEmail}
                  onChange={e => setLandlordEmail(e.target.value)}
                  disabled={sending}
                />
                {result && ERRORS[result] && (
                  <p className="text-destructive">{ERRORS[result]}</p>
                )}
                <Button type="submit" size="sm" disabled={sending}>
                  {sending ? (
                    <>
                      <Spinner className="mr-2 animate-" />
                      {M.sending}
                    </>
                  ) : (
                    M.sendInvite
                  )}
                </Button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
